import { Check, Palmtree, Phone } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { PageHero } from "@/components/layout/PageHero";
import { SectionHead, Reveal } from "@/components/layout/Motion";
import { LeadForm } from "@/components/layout/LeadForm";
import { PaymentOptions } from "@/components/layout/CtaBlocks";
import { PACKAGES } from "@/data/content";
import { PHONE_TEL } from "@/lib/site";

const INCLUDED = ["Round-trip flights", "Hand-picked hotel stay", "Airport transfers where listed", "Daily breakfast on most packages", "24/7 support while you travel", "One specialist from quote to return"];
const FIELDS = [
  { name: "name", label: "Full name", required: true }, { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone number", type: "tel", required: true }, { name: "dates", label: "Travel dates", placeholder: "e.g. Dec 20 – Dec 28" },
  { name: "travelers", label: "Travelers", options: ["1", "2", "3", "4", "5", "6", "7", "8"] },
  { name: "budget", label: "Budget range", options: ["$500–$1,000", "$1,000–$2,000", "$2,000–$3,500", "$3,500–$5,000", "$5,000+"] },
  { name: "package_type", label: "Package type", options: ["All-Inclusive", "Family Vacation", "Honeymoon", "Luxury", "Adventure", "City Break"], full: true },
];

export default function Packages() {
  return (
    <Layout>
      <PageHero testId="packages-hero" image="/img/pk/puntacana.jpg" eyebrow="Holiday packages" icon={Palmtree} line1="Top holiday packages —" line2="flights, hotels & more." text="Compare hand-picked packages our experts book most. Get a free quote, or call us now to lock pricing."
        secondary={{ href: "#package-quote", label: "Get a free quote" }} />
      <section className="bg-sand py-16">
        <div className="mx-auto max-w-[1320px] px-4 sm:px-8">
          <SectionHead eyebrow="Packages" title="Most-booked getaways" text="Inclusions are typical examples — your specialist confirms exact details and pricing." />
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.name} delay={(i % 4) * 0.05} className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white hover:shadow-xl" data-testid={`package-card-${i}`}>
                <div className="aspect-[4/3] overflow-hidden"><img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-display text-lg font-bold text-ink">{p.name}</h3>
                  <ul className="mt-3 flex-1 space-y-1.5 text-sm text-slate-600">{p.perks.map((x) => <li key={x} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{x}</li>)}</ul>
                  <a href={PHONE_TEL} className="mt-4 flex items-center justify-center gap-1.5 rounded-lg bg-brand-soft py-2.5 text-xs font-bold uppercase text-brand hover:bg-brand hover:text-white"><Phone className="h-3.5 w-3.5" />Call for price</a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white py-16">
        <div className="mx-auto max-w-[1320px] px-4 sm:px-8">
          <SectionHead title="What’s included in every package" text="Standard inclusions across our top-selling packages:" />
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{INCLUDED.map((x) => <p key={x} className="flex items-center gap-3 rounded-xl bg-sand p-4 text-sm font-semibold text-ink"><Check className="h-5 w-5 text-coral" />{x}</p>)}</div>
        </div>
      </section>
      <section id="package-quote" className="bg-brand-soft py-16">
        <div className="mx-auto grid max-w-[1320px] gap-10 px-4 sm:px-8 lg:grid-cols-2">
          <SectionHead title="Get a free package quote." text="Tell us where you’d like to go and an expert will prepare a custom itinerary with pricing." />
          <Reveal className="rounded-2xl bg-white p-6 shadow-sm sm:p-8"><LeadForm testId="package-form" source="packages" fields={FIELDS} submitLabel="Get free quote" /></Reveal>
        </div>
      </section>
      <PaymentOptions />
    </Layout>
  );
}
